import { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../api';

const emptyDeliveryPersonForm = () => ({
  fullName: '',
  phone: '',
  email: '',
  licenseNumber: '',
  vehicleType: '',
  vehicleNumber: '',
  coverageZones: [],
  isActive: true,
  isAvailable: true,
});

/**
 * Owns the delivery-person roster: list, CRUD, and the Add/Edit modal form.
 * This is the single source of truth for `deliveryPersons` — the Orders tab
 * (which assigns a delivery person to a shipped order) reads it and its own
 * fetch trigger from here rather than keeping a second copy.
 */
export default function useDelivery({ activeTab, showNotification }) {
  const [deliveryPersons, setDeliveryPersons] = useState([]);
  const [showDeliveryPersonModal, setShowDeliveryPersonModal] = useState(false);
  const [editingDeliveryPerson, setEditingDeliveryPerson] = useState(null);
  const [deliveryPersonForm, setDeliveryPersonForm] = useState(emptyDeliveryPersonForm());

  const fetchDeliveryPersons = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/delivery/persons`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        setDeliveryPersons(Array.isArray(data) ? data : (data.deliveryPersons || []));
      }
    } catch (error) {
      console.error('Error fetching delivery persons:', error);
    }
  };

  // Fetches on the Delivery tab itself (roster management) — previously this
  // only ever ran when the Orders tab was visited first, so opening Delivery
  // directly showed an empty roster until Orders had been opened at least once.
  useEffect(() => {
    if (activeTab === 'delivery') fetchDeliveryPersons();
  }, [activeTab]);

  const closeDeliveryPersonModal = () => {
    setShowDeliveryPersonModal(false);
    setEditingDeliveryPerson(null);
    setDeliveryPersonForm(emptyDeliveryPersonForm());
  };

  const openAddDeliveryPerson = () => {
    setEditingDeliveryPerson(null);
    setDeliveryPersonForm(emptyDeliveryPersonForm());
    setShowDeliveryPersonModal(true);
  };

  const handleEditDeliveryPerson = (person) => {
    setEditingDeliveryPerson(person);
    setDeliveryPersonForm({
      fullName: person.fullName || '',
      phone: person.phone || '',
      email: person.email || '',
      licenseNumber: person.licenseNumber || '',
      vehicleType: person.vehicleType || '',
      vehicleNumber: person.vehicleNumber || '',
      coverageZones: person.coverageZones || [],
      isActive: person.isActive !== undefined ? person.isActive : true,
      isAvailable: person.isAvailable !== undefined ? person.isAvailable : true,
    });
    setShowDeliveryPersonModal(true);
  };

  const handleCreateDeliveryPerson = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/delivery/persons`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(deliveryPersonForm),
      });
      if (response.ok) {
        await fetchDeliveryPersons();
        closeDeliveryPersonModal();
        showNotification('Delivery person created successfully', 'success');
      } else {
        const error = await response.json();
        showNotification(error.error || 'Failed to create delivery person', 'error');
      }
    } catch (error) {
      console.error('Error creating delivery person:', error);
      showNotification('Network error while creating delivery person', 'error');
    }
  };

  const handleUpdateDeliveryPerson = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/delivery/persons/${editingDeliveryPerson.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(deliveryPersonForm),
      });
      if (response.ok) {
        await fetchDeliveryPersons();
        closeDeliveryPersonModal();
        showNotification('Delivery person updated successfully', 'success');
      } else {
        const error = await response.json();
        showNotification(error.error || 'Failed to update delivery person', 'error');
      }
    } catch (error) {
      console.error('Error updating delivery person:', error);
      showNotification('Network error while updating delivery person', 'error');
    }
  };

  const handleDeleteDeliveryPerson = async (personId) => {
    if (!window.confirm('Are you sure you want to delete this delivery person?')) return;
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/delivery/persons/${personId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        await fetchDeliveryPersons();
        showNotification('Delivery person deleted successfully', 'success');
      } else {
        const error = await response.json();
        showNotification(error.error || 'Failed to delete delivery person', 'error');
      }
    } catch (error) {
      console.error('Error deleting delivery person:', error);
      showNotification('Network error while deleting delivery person', 'error');
    }
  };

  const handleToggleDeliveryPersonStatus = async (personId, currentStatus) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/delivery/persons/${personId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !currentStatus }),
      });
      if (response.ok) {
        await fetchDeliveryPersons();
        showNotification(`Delivery person ${!currentStatus ? 'activated' : 'paused'} successfully`, 'success');
      } else {
        const error = await response.json();
        showNotification(error.error || 'Failed to update delivery person status', 'error');
      }
    } catch (error) {
      console.error('Error toggling delivery person status:', error);
      showNotification('Network error while updating delivery person status', 'error');
    }
  };

  return {
    deliveryPersons, fetchDeliveryPersons,
    showDeliveryPersonModal, editingDeliveryPerson, deliveryPersonForm, setDeliveryPersonForm,
    openAddDeliveryPerson, closeDeliveryPersonModal, handleEditDeliveryPerson,
    handleCreateDeliveryPerson, handleUpdateDeliveryPerson, handleDeleteDeliveryPerson, handleToggleDeliveryPersonStatus,
  };
}
